import { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { ClientBankAccountsLists, IsTodayDate, Box, withStyles } from 'vopay';
import { Title } from 'UI';
import { format } from 'date-fns';

import { useTranslation } from 'react-i18next';
import { useDataFetchIndicator } from '@/hooks';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import { useClientData } from '@/providers/ClientData.provider';
import { useNavMenu } from '@/providers/NavMenu.provider';
import { useOpenModule } from '@/providers/OpenModule.provider';
import { useUserSettingsProvider } from '@/providers/UserSettings.provider';

import useFetchClientAccountList from '@/hooks/useApi/useFetchClientAccountList';
import useFetchClientBankAccounts from '@/hooks/useApi/useFetchClientBankAccounts';
import useFetchDefaultBankAccount from '@/hooks/useApi/useFetchDefaultBankAccount';
import usePostWithdrawMyAccount from '@/hooks/useApi/usePostWithdrawMyAccount';
import usePostWithdrawMyAccountSchedule from '@/hooks/useApi/usePostWithdrawMyAccountSchedule';
import WithdrawAccountForm from './WithdrawAccountForm';
import { MyAccountFormData } from '../types';
import styles from '@/styles/PaymentAndCollection/PaymentAndCollection.module.scss';

const cn = withStyles(styles);

type LocationState = {
  currency?: string;
  type?: string;
};

export default function WithdrawAccountPage() {
  const { t } = useTranslation('translations', { keyPrefix: 'Accounts.WithdrawMyAccount.Form' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });
  const { state } = useLocation<LocationState>();
  const { userSettings } = useUserSettingsProvider();
  const history = useHistory();
  const alertDialog = useAlertDialog();
  const [, setPending] = useDataFetchIndicator();

  const {
    response: { clientAccountsEnabled, Country },
  } = useClientData();
  const { getModuleTitle, setSubNavActiveModule } = useNavMenu();
  const { setOpenModule } = useOpenModule();

  const selectedCurrency = state?.currency ?? (Country === 'US' ? 'USD' : 'CAD');

  const [, , , fetchDefaultBankAccountAsync] = useFetchDefaultBankAccount({});
  const [, , , fetchBankAccounts] = useFetchClientBankAccounts({});
  const [, , , fetchClientAccounts] = useFetchClientAccountList({});
  const [, , , postWithdrawMyAccountAsync] = usePostWithdrawMyAccount({});
  const [, , , postWithdrawMyAccountScheduleAsync] = usePostWithdrawMyAccountSchedule({});
  const [selectedBank, setSelectedBank] = useState<ClientBankAccountsLists | undefined>();

  const [formData, setFormData] = useState<MyAccountFormData>({
    Currency: selectedCurrency,
    PaymentMethod: 'Bank Account',
    Account: '',
    Amount: '0.00',
    ScheduleType: 'single',
    TakeFromAccountOn: String(Date.now()),
    Frequency: 'monthly',
    SemiMonthlyDates: '',
    StartDate: String(Date.now()),
    Ending: 'End Date',
    EndDate: '',
    Description: '',
    MessageForRecipient: '',
    Notes: 'Withdraw My Account',
    EndingAfterPayments: '',
    ClientAccount: '',
    TransactionLabel: '',
    IdentificationNumber: '',
  });

  useEffect(() => {
    if (clientAccountsEnabled) return;

    async function initBankAccount() {
      try {
        const defaultRes = await fetchDefaultBankAccountAsync({ Timezone: userSettings.timezone });
        if (!defaultRes.Success) throw new Error(defaultRes.ErrorMessage);

        const operationalBank = Object.values(defaultRes.DefaultOperationalBankAccount || {}).find(
          (b) => b.Currency === selectedCurrency,
        );

        if (!operationalBank) return;

        const bankRes = await fetchBankAccounts({
          Token: operationalBank.Token,
          ClientControlled: true,
          Timezone: userSettings.timezone,
        });

        if (!bankRes.Success) throw new Error(bankRes.ErrorMessage);

        const bank = bankRes.BankAccounts[0];
        if (!bank) return;
        setSelectedBank(bank);
        setFormData((prev) => ({
          ...prev,
          Account: `${bank.Currency} ${bank.AccountHolderName} ${bank.AccountNumber}`,
        }));
      } catch (error) {
        alertDialog.error(tGlobal('error'), error instanceof Error ? error.message : String(error));
      }
    }

    initBankAccount();
  }, []);

  useEffect(() => {
    if (!clientAccountsEnabled) return;

    async function initClientAccount() {
      try {
        const clientAccountsRes = await fetchClientAccounts({ ClientType: '' });
        if (!clientAccountsRes.Success) throw new Error(clientAccountsRes.ErrorMessage);

        const primaryClientAccount = Object.values(clientAccountsRes.ClientAccounts || {}).find(
          (obj) => obj.IsPrimaryAccount === '1',
        );

        if (!primaryClientAccount) return;

        setFormData((prev) => ({ ...prev, ClientAccount: primaryClientAccount.ClientAccountID }));

        const bankAccountsRes = await fetchBankAccounts({
          ClientAccountID: primaryClientAccount.ClientAccountID,
          Timezone: userSettings.timezone,
        });

        if (!bankAccountsRes.Success) throw new Error(bankAccountsRes.ErrorMessage);

        const defaultBankAccount = Object.values(bankAccountsRes.BankAccounts || {}).find(
          (obj) => obj.IsDefault === '1',
        );

        if (!defaultBankAccount) {
          alertDialog.error(
            tGlobal('error'),
            t('ErrorMessage.noDefaultBankAccount', {
              defaultValue: 'There is no default bank account set up for client account ID: {{clientAccountID}}',
              clientAccountID: primaryClientAccount.ClientAccountID,
            }),
          );
        }

        setSelectedBank(defaultBankAccount);
      } catch (error) {
        alertDialog.error(tGlobal('error'), error instanceof Error ? error.message : String(error));
      }
    }

    initClientAccount();
  }, []);

  const handleCurrencyChange = async (currency: string) => {
    if (clientAccountsEnabled) return;

    try {
      const defaultRes = await fetchDefaultBankAccountAsync({ Timezone: userSettings.timezone });
      if (!defaultRes.Success) throw new Error(defaultRes.ErrorMessage);

      const operationalBank = Object.values(defaultRes.DefaultOperationalBankAccount || {}).find(
        (b) => b.Currency === currency,
      );

      if (!operationalBank) return;

      const res = await fetchBankAccounts({
        Token: operationalBank.Token,
        ClientControlled: true,
        Timezone: userSettings.timezone,
      });

      if (!res.Success) throw new Error(res.ErrorMessage);

      const bank = res.BankAccounts[0];
      if (!bank) return;
      setSelectedBank(bank);
      setFormData((prev) => ({
        ...prev,
        Account: `${bank.Currency} ${bank.AccountHolderName} ${bank.AccountNumber}`,
      }));
    } catch (error) {
      alertDialog.error(tGlobal('error'), error instanceof Error ? error.message : String(error));
    }
  };

  const createWithdrawal = async (values: MyAccountFormData) => {
    if (!selectedBank) return;

    const amount = Number(String(values.Amount || '0').replaceAll(',', ''));
    const scheduleType = values.ScheduleType || 'single';
    const takeFromAccountOn = values.TakeFromAccountOn || String(Date.now());
    const startDate = values.StartDate || String(Date.now());
    const currency = values.Currency || '';

    const accountQuery = new URLSearchParams({
      accountName: selectedBank.AccountHolderName,
      accountDetails: selectedBank.AccountNumber,
    });

    const isImmediate = scheduleType === 'single' && IsTodayDate(format(Number(takeFromAccountOn), 'yyyy-MM-dd'));

    if (isImmediate) {
      try {
        setPending(true);
        const response = await postWithdrawMyAccountAsync({
          Token: clientAccountsEnabled ? '' : selectedBank.Token || '',
          Amount: amount,
          Currency: currency,
          Notes: values.Notes || '',
          ClientAccountID: clientAccountsEnabled ? values.ClientAccount : '',
        });
        if (!response.Success) throw new Error(response.ErrorMessage);

        setOpenModule('statements');
        setSubNavActiveModule('transactionsHistory');
        history.push(
          `/my-account-confirmation/${response.TransactionID}/${currency}/withdraw/transaction?${accountQuery}`,
        );
      } catch (error) {
        alertDialog.error(tGlobal('error'), error instanceof Error ? error.message : String(error));
      } finally {
        setPending(false);
      }
    } else {
      try {
        setPending(true);
        const response = await postWithdrawMyAccountScheduleAsync({
          Amount: amount,
          Frequency: scheduleType,
          NameOfFrequency: values.Frequency || undefined,
          SemiMonthlyFrequencyType: values.SemiMonthlyDates || '',
          ScheduleStartDate:
            scheduleType === 'recurring'
              ? format(Number(startDate), 'yyyy-MM-dd')
              : format(Number(takeFromAccountOn), 'yyyy-MM-dd'),
          ScheduleEndDate:
            values.Ending === 'End Date' && scheduleType === 'recurring'
              ? format(Number(values.EndDate), 'yyyy-MM-dd')
              : '',
          Currency: currency,
          EndingAfterPayments: values.Ending === 'Payments' ? values.EndingAfterPayments || '' : '',
          Description: values.Description || '',
        });
        if (!response.Success) throw new Error(response.ErrorMessage);

        setOpenModule('statements');
        history.push(
          `/my-account-confirmation/${response.ScheduledTransactionID}/${currency}/withdraw/scheduled?${accountQuery}`,
        );
      } catch (error) {
        alertDialog.error(tGlobal('error'), error instanceof Error ? error.message : String(error));
      } finally {
        setPending(false);
      }
    }
  };

  return (
    <div className={cn('pageContainer')}>
      <div className={cn('stickyHeader')}>
        <Box jc="center" width="100%">
          <Box flx="col" jc="center" maxwidth="1121px" boxWidth="100%" p="md">
            <Title>{getModuleTitle()}</Title>
          </Box>
        </Box>
      </div>

      <Box jc="center" width="100%">
        <Box flx="col" jc="center" maxwidth="1121px" boxWidth="100%" p="md">
          <WithdrawAccountForm
            defaultValues={formData}
            onSubmit={createWithdrawal}
            onCurrencyChange={handleCurrencyChange}
            hasBankAccount={!!selectedBank}
          />
        </Box>
      </Box>
    </div>
  );
}
